import React from 'react';
import styles from './EmptySectionLayout.module.css';

/**
 * AB#1675 — Reusable empty-state layout for My Business sub-sections.
 *
 * Used by sections that don't have data yet (no branches, no staff, etc.).
 * Matches the Step 3 onboarding mockup: an empty-hero card with a tasteful
 * illustration, a "you haven't yet" headline, value-prop sub-copy, primary
 * + secondary CTAs, three benefit cards, and a skip-hint at the bottom.
 *
 * The same component is used both during onboarding (via /my-business)
 * and on the standalone product pages (e.g. /staff) — once data exists,
 * the empty state is replaced with the real list.
 */

export interface EmptySectionBenefit {
    icon: string;     // emoji or icon glyph
    title: string;
    description: string;
}

export interface EmptySectionLayoutProps {
    /** Section H1 (e.g. "Manage Staff"). */
    title: string;
    /** Subtitle below the title. */
    subtitle: string;
    /** Headline shown inside the empty hero card. */
    emptyHeadline: string;
    /** Empty-state body copy explaining the section's value. */
    emptyDescription: string;
    /** Single illustration glyph at the top of the empty hero. */
    illustration: string;
    /** Primary CTA label (e.g. "+ Add your first staff"). */
    primaryCtaLabel: string;
    /** Optional secondary CTA label (e.g. "Invite via email"). */
    secondaryCtaLabel?: string;
    /** Three benefit cards explaining what they get from this section. */
    benefits: [EmptySectionBenefit, EmptySectionBenefit, EmptySectionBenefit];
    /** Skip-hint shown at the bottom (e.g. "explore the Calendar first"). */
    skipHint?: React.ReactNode;
    /** Optional click handlers (decorative for now during the founder-review build). */
    onPrimary?: () => void;
    onSecondary?: () => void;
    /** Optional list of tabs displayed under the page header (visible from day one). */
    tabs?: string[];
}

const EmptySectionLayout: React.FC<EmptySectionLayoutProps> = ({
    title, subtitle, emptyHeadline, emptyDescription, illustration,
    primaryCtaLabel, secondaryCtaLabel, benefits, skipHint, onPrimary, onSecondary, tabs,
}) => {
    return (
        <div>
            <header className={styles.header}>
                <div>
                    <h1>{title}</h1>
                    <p>{subtitle}</p>
                </div>
                <button type="button" className={styles.btnPrimary} onClick={onPrimary}>
                    <span className={styles.plus} aria-hidden="true">+</span>
                    {primaryCtaLabel}
                </button>
            </header>

            {tabs && tabs.length > 0 && (
                <nav className={styles.tabs} aria-label={`${title} sections`}>
                    {tabs.map((tab, i) => (
                        <span key={tab} className={`${styles.tab} ${i === 0 ? styles.tabActive : ''}`}>
                            {tab}
                        </span>
                    ))}
                </nav>
            )}

            <div className={styles.heroCard}>
                <div className={styles.illustration} aria-hidden="true">{illustration}</div>
                <h2 className={styles.heroHeadline}>{emptyHeadline}</h2>
                <p className={styles.heroDescription}>{emptyDescription}</p>

                <div className={styles.actions}>
                    <button type="button" className={styles.btnPrimary} onClick={onPrimary}>
                        <span className={styles.plus} aria-hidden="true">+</span>
                        {primaryCtaLabel}
                    </button>
                    {secondaryCtaLabel && (
                        <button type="button" className={styles.btnSecondary} onClick={onSecondary}>
                            {secondaryCtaLabel}
                        </button>
                    )}
                </div>

                <div className={styles.benefits}>
                    {benefits.map(b => (
                        <div key={b.title} className={styles.benefit}>
                            <div className={styles.benefitIcon} aria-hidden="true">{b.icon}</div>
                            <h4>{b.title}</h4>
                            <p>{b.description}</p>
                        </div>
                    ))}
                </div>
            </div>

            {skipHint && (
                <div className={styles.skipHint}>{skipHint}</div>
            )}
        </div>
    );
};

export default EmptySectionLayout;
